import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { carrierTrackingUrl, carrierFor } from '../lib/carrierLink';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { User, Package, MapPin, Star, LogOut, ChevronDown, ChevronUp, Truck, Clock, Edit2, Save, ArrowLeft, Gift, RotateCcw, CreditCard, AlertTriangle } from 'lucide-react';
import { US_STATES, ACCOUNT_TYPES, INDUSTRIES } from '../lib/constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: 'bg-yellow-100 text-yellow-700',
  placed: 'bg-yellow-100 text-yellow-700',
  processing: 'bg-blue-100 text-blue-700',
  shipped: 'bg-green-100 text-green-700',
  confirmed: 'bg-green-100 text-green-700',
  cancellation_requested: 'bg-red-100 text-red-700',
  cancelled: 'bg-red-100 text-red-700',
};

const STATUS_LABEL = {
  pending: 'placed',
  cancellation_requested: 'cancellation requested',
};

const MENU_ITEMS = [
  { key: 'account', label: 'Account Details', icon: User },
  { key: 'orders', label: 'Orders', icon: Package },
  { key: 'addresses', label: 'Addresses', icon: MapPin },
  { key: 'points', label: 'Points & Rewards', icon: Star },
  { key: 'logout', label: 'Logout', icon: LogOut },
];

const REWARDS = [
  { name: '$5 Off Next Order', points: 500, value: 5 },
  { name: '$10 Off Next Order', points: 1000, value: 10 },
  { name: '$25 Off Next Order', points: 2500, value: 25 },
  { name: 'Free Shipping', points: 300, value: 0 },
];

export default function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get('section') || '';
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate('/login');
  }, [user, authLoading, navigate]);

  const setSection = (s) => {
    if (s === 'logout') { logout(); navigate('/'); return; }
    if (s === '') setSearchParams({});
    else setSearchParams({ section: s });
  };

  if (!user) return null;

  const showSidebar = !!section;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="dashboard-page">
      {!showSidebar ? (
        <>
          <div className="mb-8">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[hsl(210,45%,40%)] mb-2 font-body">Account</p>
            <h1 className="text-4xl tracking-tight font-light text-foreground font-heading">Dashboard</h1>
            <p className="mt-2 text-base text-muted-foreground font-body">Welcome back, {user.name}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {MENU_ITEMS.map(item => (
              <Card key={item.key} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSection(item.key)} data-testid={`dash-${item.key}`}>
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(210,25%,92%)] flex items-center justify-center">
                    <item.icon className="h-5 w-5 text-[hsl(210,45%,35%)]" />
                  </div>
                  <span className="font-heading font-medium text-foreground">{item.label}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <div className="flex gap-8">
          <aside className="hidden md:block w-48 flex-shrink-0 space-y-1">
            <Button variant="ghost" size="sm" className="w-full justify-start text-sm font-medium" onClick={() => setSection('')} data-testid="dash-back">
              <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
            </Button>
            <Separator className="my-2" />
            {MENU_ITEMS.map(item => (
              <Button key={item.key} variant={section === item.key ? "secondary" : "ghost"} size="sm" className="w-full justify-start text-sm"
                onClick={() => setSection(item.key)} data-testid={`side-${item.key}`}>
                <item.icon className="h-4 w-4 mr-2" />{item.label}
              </Button>
            ))}
          </aside>
          <div className="flex-1 min-w-0">
            {section === 'account' && <AccountSection />}
            {section === 'orders' && <OrdersSection />}
            {section === 'addresses' && <AddressesSection />}
            {section === 'points' && <PointsSection />}
          </div>
        </div>
      )}
    </div>
  );
}

function AccountSection() {
  const { resendEmailVerification } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [password, setPassword] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/user/profile`, { withCredentials: true }).then(r => { setProfile(r.data); setForm(r.data); }).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!password) { toast.error('Enter password to confirm changes'); return; }
    try {
      await axios.patch(`${API}/user/profile`, { ...form, current_password: password }, { withCredentials: true });
      toast.success('Profile updated');
      setEditing(false); setPassword('');
      axios.get(`${API}/user/profile`, { withCredentials: true }).then(r => { setProfile(r.data); setForm(r.data); });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (!profile) return <div className="h-40 bg-muted animate-pulse rounded" />;

  const handleResendVerification = async () => {
    setResending(true);
    const r = await resendEmailVerification();
    setResending(false);
    if (r.success) toast.success('Verification email sent. Check your inbox.');
    else toast.error(r.error || 'Could not send verification email');
  };

  const textFields = [
    { key: 'first_name', label: 'First Name' }, { key: 'last_name', label: 'Last Name' },
    { key: 'email', label: 'Email', readonly: true }, { key: 'phone', label: 'Phone' },
  ];

  return (
    <div data-testid="account-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-heading font-light text-foreground">Account Details</h2>
        {!editing && <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-account"><Edit2 className="h-3.5 w-3.5 mr-1" /> Edit</Button>}
      </div>
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {textFields.map(f => (
              <div key={f.key} className="space-y-1">
                <Label className="text-xs text-muted-foreground font-body">{f.label}</Label>
                {editing && !f.readonly ? (
                  <Input value={form[f.key] || ''} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))} data-testid={`edit-${f.key}`} />
                ) : f.key === 'email' ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-foreground" data-testid="account-email-value">{profile.email || '—'}</p>
                    {profile.email_verified ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="account-email-verified-badge">
                        Verified
                      </span>
                    ) : (
                      <>
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200" data-testid="account-email-unverified-badge">
                          Unverified
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleResendVerification}
                          disabled={resending}
                          className="h-7 text-xs px-2.5 border-[hsl(210,45%,40%)]/30 text-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,40%)]/5"
                          data-testid="account-email-verify-btn"
                        >
                          {resending ? 'Sending…' : 'Verify'}
                        </Button>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-sm font-medium text-foreground">{profile[f.key] || '—'}</p>
                )}
              </div>
            ))}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground font-body">Account Type</Label>
              {editing ? (
                <Select value={form.account_type || ''} onValueChange={v => setForm(prev => ({ ...prev, account_type: v }))}>
                  <SelectTrigger data-testid="edit-account_type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm font-medium text-foreground">{profile.account_type || '—'}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground font-body">Industry</Label>
              {editing ? (
                <Select value={form.industry || ''} onValueChange={v => setForm(prev => ({ ...prev, industry: v }))}>
                  <SelectTrigger data-testid="edit-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm font-medium text-foreground">{profile.industry || '—'}</p>
              )}
            </div>
          </div>
          {editing && (
            <div className="pt-4 border-t space-y-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground font-body">Confirm Password *</Label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter current password" data-testid="confirm-password" />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSave} className="bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white" data-testid="save-account"><Save className="h-3.5 w-3.5 mr-1" /> Save</Button>
                <Button variant="outline" onClick={() => { setEditing(false); setForm(profile); setPassword(''); }}>Cancel</Button>
              </div>
            </div>
          )}
          <div className="pt-4 border-t" data-testid="marketing-consent-section">
            <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground font-body mb-3">Communication Preferences</p>
            <label className="flex items-start gap-2 text-sm font-body cursor-pointer">
              <Checkbox
                checked={!!profile.marketing_consent}
                onCheckedChange={async (v) => {
                  try {
                    await axios.patch(`${API}/user/marketing-consent`, { enabled: v === true }, { withCredentials: true });
                    setProfile(p => p ? { ...p, marketing_consent: v === true } : p);
                    toast.success(v ? 'Subscribed to marketing emails' : 'Unsubscribed');
                  } catch { toast.error('Could not update preference'); }
                }}
                className="mt-0.5"
                data-testid="marketing-consent-toggle"
              />
              <span className="text-foreground">
                Send me marketing emails about <strong>promo codes, restocks, and new product releases.</strong>
                <span className="block text-xs text-muted-foreground mt-0.5 italic">Optional · You can opt out any time.</span>
              </span>
            </label>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const PAYMENT_LABEL = {
  card: 'Credit / Debit Card',
  crypto: 'Crypto',
  complimentary: 'Complimentary',
};

function formatAddr(addr) {
  if (!addr || !addr.address) return null;
  const name = [addr.first_name, addr.last_name].filter(Boolean).join(' ');
  const line1 = addr.address;
  const line2 = addr.address_line_2;
  const line3 = [addr.city, addr.state, addr.postal_code].filter(Boolean).join(', ');
  return { name, company: addr.company, line1, line2, line3, phone: addr.phone, email: addr.email };
}

function OrdersSection() {
  const { addToCart, clearCart, cart } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [reorderTarget, setReorderTarget] = useState(null); // order pending decision
  const [cancelTarget, setCancelTarget] = useState(null); // order pending cancellation confirm

  const fetchOrders = () => axios.get(`${API}/orders`, { withCredentials: true })
    .then(r => setOrders(r.data.orders))
    .catch(() => {})
    .finally(() => setLoading(false));

  useEffect(() => { fetchOrders(); }, []);

  const reorderItems = async (items) => {
    let okCount = 0;
    for (const item of items) {
      const r = await addToCart(item.product_id, item.quantity, item.variant);
      if (r.success) okCount++;
    }
    if (okCount) toast.success(`${okCount} item${okCount > 1 ? 's' : ''} added to cart`);
    if (okCount < items.length) toast.error(`${items.length - okCount} item${items.length - okCount > 1 ? 's' : ''} could not be added (stock or visibility)`);
    navigate('/checkout');
  };

  const handleOrderAgain = (order) => {
    const hasItems = cart?.items?.length > 0;
    if (!hasItems) {
      reorderItems(order.items);
    } else {
      setReorderTarget(order);
    }
  };

  const confirmAdd = async () => {
    const o = reorderTarget; setReorderTarget(null);
    if (o) await reorderItems(o.items);
  };
  const confirmReplace = async () => {
    const o = reorderTarget; setReorderTarget(null);
    if (!o) return;
    try { await clearCart(); } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('clearCart before reorder failed:', e?.message);
    }
    await reorderItems(o.items);
  };

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-muted animate-pulse rounded" />)}</div>;

  return (
    <div data-testid="orders-section">
      <h2 className="text-2xl font-heading font-light text-foreground mb-6">Orders</h2>
      {orders.length === 0 ? (
        <div className="text-center py-12"><Package className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground">No orders yet.</p></div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="hidden sm:grid grid-cols-6 gap-4 px-4 py-3 bg-muted/50 text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b text-center">
              <span>Order No.</span><span>Date Placed</span><span>Status</span><span>Total</span><span>Tracking</span><span>Expand</span>
            </div>
            {orders.map(order => {
              const ship = formatAddr(order.shipping_address);
              const bill = formatAddr(order.billing_address);
              const paymentLabel = PAYMENT_LABEL[order.payment_method] || (order.total === 0 ? 'Complimentary' : 'Credit / Debit Card');
              const trackingHref = carrierTrackingUrl(order.tracking_number, order.shipping_carrier_code);
              return (
                <div key={order.id} className="border-b last:border-0" data-testid={`order-row-${order.id}`}>
                  <button
                    type="button"
                    className="w-full text-center grid grid-cols-2 sm:grid-cols-6 gap-2 sm:gap-4 px-4 py-3 items-center hover:bg-muted/20"
                    onClick={() => setExpanded(p => p === order.id ? null : order.id)}
                  >
                    <span className="text-sm font-medium text-foreground">#{order.id}</span>
                    <span className="text-xs sm:text-sm text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</span>
                    <Badge className={`${STATUS_STYLES[order.status] || 'bg-gray-100 text-gray-700'} text-xs justify-self-center`}>{STATUS_LABEL[order.status] || order.status}</Badge>
                    <span className="text-sm font-medium">${order.total?.toFixed(2)}</span>
                    <span className="text-xs sm:text-[11px] font-mono break-all" data-testid={`order-tracking-${order.id}`}>
                      {order.tracking_number ? (
                        trackingHref ? (
                          <a
                            href={trackingHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-[hsl(210,45%,40%)] hover:underline"
                            title={`Track via ${carrierFor(order.tracking_number) || 'carrier'}`}
                            data-testid={`order-tracking-link-${order.id}`}
                          >{order.tracking_number}</a>
                        ) : <span>{order.tracking_number}</span>
                      ) : <span className="italic text-muted-foreground">Pre-Shipment</span>}
                    </span>
                    <div className="col-span-2 sm:col-span-1">{expanded === order.id ? <ChevronUp className="h-4 w-4 inline text-muted-foreground" /> : <ChevronDown className="h-4 w-4 inline text-muted-foreground" />}</div>
                  </button>
                  {expanded === order.id && (
                    <div className="px-4 pb-4 bg-muted/10 space-y-4">
                      <div className="space-y-1 pt-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Items</p>
                        {order.items?.map((item, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <Link
                              to={`/product/${item.product_id}`}
                              className="text-[hsl(210,45%,40%)] hover:underline"
                              data-testid={`order-item-link-${order.id}-${i}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {item.name}{item.variant ? ` (${item.variant})` : ''} × {item.quantity}
                            </Link>
                            <span>${item.subtotal?.toFixed(2)}</span>
                          </div>
                        ))}
                        {order.shipping_cost !== undefined && (
                          <div className="flex justify-between text-xs text-muted-foreground pt-1">
                            <span>{order.shipping_label || 'Shipping'}</span>
                            <span>{order.shipping_cost === 0 ? 'FREE' : `$${order.shipping_cost.toFixed(2)}`}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm font-semibold pt-2 border-t" data-testid={`order-total-${order.id}`}>
                          <span>Order Total</span>
                          <span>${order.total?.toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t">
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Shipping Address</p>
                          {ship ? (
                            <div className="text-xs text-foreground font-body space-y-0.5">
                              {ship.name && <p className="font-medium">{ship.name}</p>}
                              {ship.company && <p className="text-muted-foreground">{ship.company}</p>}
                              <p>{ship.line1}</p>
                              {ship.line2 && <p>{ship.line2}</p>}
                              <p>{ship.line3}</p>
                            </div>
                          ) : <p className="text-xs text-muted-foreground italic">—</p>}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Billing Address</p>
                          {bill ? (
                            <div className="text-xs text-foreground font-body space-y-0.5">
                              {bill.name && <p className="font-medium">{bill.name}</p>}
                              {bill.company && <p className="text-muted-foreground">{bill.company}</p>}
                              <p>{bill.line1}</p>
                              {bill.line2 && <p>{bill.line2}</p>}
                              <p>{bill.line3}</p>
                              {bill.phone && <p className="text-muted-foreground">Phone: {bill.phone}</p>}
                              {bill.email && <p className="text-muted-foreground">Email: {bill.email}</p>}
                            </div>
                          ) : <p className="text-xs text-muted-foreground italic">—</p>}
                        </div>
                      </div>
                      <div className="pt-2 border-t" data-testid={`order-payment-${order.id}`}>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Payment Method</p>
                        <p className="text-sm text-foreground font-body flex items-center gap-2">
                          <CreditCard className="h-4 w-4 text-muted-foreground" /> {paymentLabel}
                        </p>
                      </div>
                      {(order.phone || order.company) && (
                        <div className="text-xs text-muted-foreground font-body pt-2 border-t">
                          {order.company && <span className="mr-3"><strong className="text-foreground">Company:</strong> {order.company}</span>}
                          {order.phone && <span><strong className="text-foreground">Phone:</strong> {order.phone}</span>}
                        </div>
                      )}
                      {order.tracking_number && (
                        <div className="flex items-center justify-center gap-2 text-sm pt-2 text-green-600">
                          <Truck className="h-4 w-4" /> Tracking:&nbsp;
                          {(() => { const u = carrierTrackingUrl(order.tracking_number, order.shipping_carrier_code); return u ? (
                            <a href={u} target="_blank" rel="noopener noreferrer" className="font-mono underline hover:text-green-700">{order.tracking_number}</a>
                          ) : <span className="font-mono">{order.tracking_number}</span>; })()}
                        </div>
                      )}
                      {order.status === 'pending' && <div className="flex items-center gap-1 text-xs text-yellow-600 pt-1"><Clock className="h-3 w-3" /> Order is being processed</div>}
                      <div className="pt-3 border-t flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5"
                          onClick={(e) => { e.stopPropagation(); handleOrderAgain(order); }}
                          data-testid={`order-again-${order.id}`}
                        >
                          <RotateCcw className="h-3.5 w-3.5" /> Order Again
                        </Button>
                        {order.status === 'shipped' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1.5 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={(e) => { e.stopPropagation(); navigate(`/contact?order=${order.id}`); }}
                            data-testid={`report-issue-${order.id}`}
                          >
                            <AlertTriangle className="h-3.5 w-3.5" /> Report an Issue
                          </Button>
                        ) : (['pending', 'placed', 'processing'].includes(order.status)) ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1.5 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={(e) => { e.stopPropagation(); setCancelTarget(order); }}
                            data-testid={`request-cancellation-${order.id}`}
                          >
                            <AlertTriangle className="h-3.5 w-3.5" /> Request Cancellation
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
      <AlertDialog open={!!reorderTarget} onOpenChange={(o) => !o && setReorderTarget(null)}>
        <AlertDialogContent data-testid="reorder-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>You already have items in your cart</AlertDialogTitle>
            <AlertDialogDescription>
              Would you like to add the items from order #{reorderTarget?.id} in addition to what's in your cart, or replace your cart with this order's items?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setReorderTarget(null)} data-testid="reorder-cancel">Cancel</AlertDialogCancel>
            <Button variant="outline" onClick={confirmReplace} data-testid="reorder-replace">Replace Cart</Button>
            <AlertDialogAction onClick={confirmAdd} className="bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white" data-testid="reorder-add">Add to Cart</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent data-testid="cancellation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Request Cancellation for Order #{cancelTarget?.id}?</AlertDialogTitle>
            <AlertDialogDescription>
              We can not confirm an order will be cancelled. If the cancellation is confirmed, it can take several business days for a refund to be processed. Do you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCancelTarget(null)} data-testid="cancellation-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const target = cancelTarget;
                setCancelTarget(null);
                if (!target) return;
                try {
                  await axios.post(`${API}/orders/${target.id}/cancel-request`, {}, { withCredentials: true });
                  toast.success('Cancellation requested');
                  fetchOrders();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to request cancellation');
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="cancellation-confirm"
            >
              Yes, Request Cancellation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AddressesSection() {
  const [addresses, setAddresses] = useState({ shipping_address: {}, billing_address: {} });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => { fetchAddresses(); }, []);
  const fetchAddresses = () => axios.get(`${API}/user/addresses`, { withCredentials: true }).then(r => setAddresses(r.data)).catch(() => {});

  const startEdit = (type) => { setEditing(type); setForm(addresses[`${type}_address`] || {}); };

  const validate = (type) => {
    const required = ['first_name', 'last_name', 'address', 'city', 'state', 'postal_code'];
    if (type === 'billing') required.push('phone', 'email');
    for (const key of required) {
      if (!form[key] || !String(form[key]).trim()) {
        toast.error(`${key.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())} is required`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate(editing)) return;
    try {
      await axios.patch(`${API}/user/addresses/${editing}`, form, { withCredentials: true });
      toast.success(`${editing} address updated`); setEditing(null); fetchAddresses();
    } catch { toast.error('Failed to update'); }
  };

  const renderAddr = (addr, isBilling = false) => {
    if (!addr || !addr.address) return <p className="text-sm text-muted-foreground italic">No address set</p>;
    const name = [addr.first_name, addr.last_name].filter(Boolean).join(' ');
    return (
      <div className="text-sm text-foreground space-y-0.5 font-body">
        {name && <p className="font-medium">{name}</p>}
        {addr.company && <p className="text-muted-foreground">{addr.company}</p>}
        <p>{addr.address}</p>
        {addr.address_line_2 && <p>{addr.address_line_2}</p>}
        <p>{[addr.city, addr.state, addr.postal_code].filter(Boolean).join(', ')}</p>
        {isBilling && addr.phone && <p className="text-muted-foreground">Phone: {addr.phone}</p>}
        {isBilling && addr.email && <p className="text-muted-foreground">Email: {addr.email}</p>}
      </div>
    );
  };

  return (
    <div data-testid="addresses-section">
      <h2 className="text-2xl font-heading font-light text-foreground mb-6">Addresses</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {['shipping', 'billing'].map(type => {
          const isBilling = type === 'billing';
          return (
            <Card key={type}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-base capitalize">{type} Address</CardTitle>
                  {editing !== type && (
                    <Button size="sm" variant="ghost" onClick={() => startEdit(type)} data-testid={`edit-${type}-addr`}><Edit2 className="h-3.5 w-3.5" /></Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editing === type ? (
                  <div className="space-y-3" data-testid={`${type}-form`}>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground font-body">First Name *</Label>
                        <Input value={form.first_name || ''} onChange={e => setForm(p => ({ ...p, first_name: e.target.value }))} data-testid={`${type}-first-name`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground font-body">Last Name *</Label>
                        <Input value={form.last_name || ''} onChange={e => setForm(p => ({ ...p, last_name: e.target.value }))} data-testid={`${type}-last-name`} />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground font-body">Company Name (Optional)</Label>
                      <Input value={form.company || ''} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} data-testid={`${type}-company`} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground font-body">Street Address *</Label>
                      <Input placeholder="1234 North 5th Street" value={form.address || ''} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} data-testid={`${type}-address`} />
                      <Input placeholder="Apartment, Suite, etc. (Optional)" value={form.address_line_2 || ''} onChange={e => setForm(p => ({ ...p, address_line_2: e.target.value }))} data-testid={`${type}-address-line-2`} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground font-body">City *</Label>
                        <Input value={form.city || ''} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} data-testid={`${type}-city`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground font-body">State *</Label>
                        <Select value={form.state || ''} onValueChange={v => setForm(p => ({ ...p, state: v }))}>
                          <SelectTrigger data-testid={`${type}-state`}><SelectValue placeholder="Select state" /></SelectTrigger>
                          <SelectContent className="max-h-64">
                            {US_STATES.map(s => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground font-body">Zip Code *</Label>
                      <Input value={form.postal_code || ''} onChange={e => setForm(p => ({ ...p, postal_code: e.target.value }))} data-testid={`${type}-zip`} />
                    </div>
                    {isBilling && (
                      <>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground font-body">Phone Number *</Label>
                          <Input type="tel" value={form.phone || ''} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} data-testid="billing-phone" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground font-body">Email Address *</Label>
                          <Input type="email" value={form.email || ''} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} data-testid="billing-email" />
                        </div>
                      </>
                    )}
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={handleSave} className="bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white" data-testid={`save-${type}-addr`}>Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : renderAddr(addresses[`${type}_address`], isBilling)}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function PointsSection() {
  const [data, setData] = useState(null);

  useEffect(() => { axios.get(`${API}/user/points`, { withCredentials: true }).then(r => setData(r.data)).catch(() => {}); }, []);

  const handleRedeem = async (reward) => {
    if (!data || data.available < reward.points) { toast.error('Not enough points'); return; }
    try {
      await axios.post(`${API}/user/points/redeem`, { points: reward.points, reward_name: reward.name }, { withCredentials: true });
      toast.success(`Redeemed ${reward.name}!`);
      axios.get(`${API}/user/points`, { withCredentials: true }).then(r => setData(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (!data) return <div className="h-40 bg-muted animate-pulse rounded" />;

  return (
    <div data-testid="points-section">
      <h2 className="text-2xl font-heading font-light text-foreground mb-6">Points & Rewards</h2>

      {/* My Points Summary */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <Card className="bg-[hsl(210,25%,97%)]">
          <CardContent className="p-5 text-center">
            <p className="text-3xl font-heading font-bold text-[hsl(210,45%,40%)]">{data.available}</p>
            <p className="text-sm font-medium text-foreground mt-1">Available Points</p>
            <p className="text-xs text-muted-foreground">{data.earned_total} Total Earned</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 text-center">
            <p className="text-3xl font-heading font-bold text-foreground">{data.redeemed}</p>
            <p className="text-sm font-medium text-foreground mt-1">Redeemed Points</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 text-center">
            <p className="text-3xl font-heading font-bold text-foreground">{data.rewards_used}</p>
            <p className="text-sm font-medium text-foreground mt-1">Used Rewards</p>
            <p className="text-xs text-muted-foreground">${data.rewards_value} Value</p>
          </CardContent>
        </Card>
      </div>

      {/* My Rewards */}
      <h3 className="font-heading font-semibold text-foreground mb-3">My Rewards</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
        {REWARDS.map(r => (
          <Card key={r.name} className={data.available >= r.points ? '' : 'opacity-50'}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Gift className="h-5 w-5 text-[hsl(210,45%,40%)]" />
                <div>
                  <p className="text-sm font-medium text-foreground">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{r.points} points</p>
                </div>
              </div>
              <Button size="sm" disabled={data.available < r.points} onClick={() => handleRedeem(r)}
                className="bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white" data-testid={`redeem-${r.points}`}>
                Claim
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Activity */}
      <h3 className="font-heading font-semibold text-foreground mb-3">Recent Activity</h3>
      {data.activities.length === 0 ? (
        <p className="text-sm text-muted-foreground">No activity yet. Place an order to earn points!</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            {data.activities.map((a, i) => (
              <div key={`${a.created_at || 'act'}-${i}`} className="flex items-center justify-between px-4 py-3 border-b last:border-0">
                <div>
                  <p className="text-sm text-foreground">{a.message}</p>
                  <p className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`text-sm font-semibold ${a.points > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {a.points > 0 ? '+' : ''}{a.points} pts
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
